/**
 * Axios instance with JWT bearer auth and silent refresh on 401.
 *
 * Tokens live in localStorage:
 *   vc_access_token   (1 h)
 *   vc_refresh_token  (7 d)
 *
 * On 401 with a refresh token present, we silently exchange it for a new
 * access token and retry the original request once. If the refresh itself
 * fails, we wipe tokens and dispatch a `vc:auth-required` event the auth
 * store listens to so it can route the user to /login.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig, AxiosHeaders } from 'axios';

const ACCESS_KEY = 'vc_access_token';
const REFRESH_KEY = 'vc_refresh_token';

export const api = axios.create({
  baseURL: '/api/v1',
  timeout: 20_000,
});

// ---- Request interceptor: attach bearer ----
api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) {
    if (!cfg.headers) cfg.headers = new AxiosHeaders();
    cfg.headers.set('Authorization', `Bearer ${token}`);
  }
  return cfg;
});

// ---- Response interceptor: refresh on 401 once ----
let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return null;
  try {
    const resp = await axios.post('/api/v1/auth/refresh', { refresh_token: refresh });
    const newAccess = resp.data.access_token as string;
    const newRefresh = resp.data.refresh_token as string;
    localStorage.setItem(ACCESS_KEY, newAccess);
    if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const data = err.response?.data as { error?: string } | undefined;
    const original = err.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (status === 401 && original && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      if (!refreshing) refreshing = refreshAccess().finally(() => { refreshing = null; });
      const newToken = await refreshing;
      if (newToken) {
        if (!original.headers) original.headers = new AxiosHeaders();
        (original.headers as AxiosHeaders).set('Authorization', `Bearer ${newToken}`);
        return api.request(original);
      }
      // Refresh failed → wipe + signal
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      window.dispatchEvent(new CustomEvent('vc:auth-required'));
    }

    // Demo-gate: shared read-only workspace blocks all mutations server-side
    // with 403 demo_readonly. Dispatch an event so a single global modal can
    // intercept it (App.vue listens) — no per-view handling required.
    if (status === 403 && data?.error === 'demo_readonly') {
      window.dispatchEvent(new CustomEvent('vc:demo-readonly'));
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  login(email: string, password: string) {
    return api.post('/auth/login', { email, password }).then((r) => r.data);
  },
  // Password-less entry to the shared read-only demo workspace. Fired by /demo
  // route when the visitor lands from the marketing CTA. The returned tokens
  // belong to demo@vistochat.com (is_demo=1), so all mutations are blocked
  // server-side by the demo-gate in requireAuth and surface as 403 demo_readonly.
  demoLogin(locale: 'en' | 'el') {
    return api.post('/auth/demo-login', { locale }).then((r) => r.data);
  },
  register(input: { email: string; password: string; full_name: string; team_name?: string; locale?: string; accept_terms: true; plan?: string }) {
    return api.post('/auth/register', input).then((r) => r.data);
  },
  resendVerification() {
    return api.post('/auth/verify/resend').then((r) => r.data);
  },
  logout() {
    return api.post('/auth/logout').catch(() => null);
  },
  me() {
    return api.get('/auth/me').then((r) => r.data);
  },
  // Persist UI-language choice for the authenticated user. Best-effort: a
  // failed call still leaves localStorage updated client-side so the user
  // sees the new locale immediately; the next successful request will retry.
  setLocale(locale: 'en' | 'el') {
    return api.patch('/auth/locale', { locale }).then((r) => r.data);
  },
  setTokens(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clearTokens() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  hasTokens() {
    return !!localStorage.getItem(ACCESS_KEY);
  },
};
